import type { ReactNode } from "react";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

/**
 * A radio group rendered as selectable cards.
 *
 * Generalises the idiom the income wizard's work-modality selector already
 * used: a visually hidden `RadioGroupItem` paired with a `Label` via `peer`.
 * Keeping the real radio underneath is not a detail — it is what preserves
 * arrow-key navigation, focus handling and screen-reader announcement. A
 * card group that only looks right is a regression, not an improvement.
 *
 * Give every option a `hint` that carries real information. A binary Si/No
 * rendered as two bare cards reads as two competing CTAs; the hint line is
 * what makes the choice legible. If there is nothing useful to say, a plain
 * radio row is the better control.
 */
export interface ChoiceCardOption<T extends string> {
  value: T;
  label: string;
  /** Secondary line. Strongly recommended — see the note above. */
  hint?: string;
  /** Optional footer, e.g. a requirements line with an icon. */
  footer?: ReactNode;
}

export interface ChoiceCardGroupProps<T extends string> {
  /** Empty string means "nothing selected yet". */
  value: T | "";
  onValueChange: (value: T) => void;
  options: ReadonlyArray<ChoiceCardOption<T>>;
  /** Used to build each card's input id; must be unique per group on a page. */
  idPrefix: string;
  /** Accessible name for the group. */
  ariaLabel: string;
  columns?: 1 | 2 | 3;
  disabled?: boolean;
  className?: string;
}

const COLUMN_CLASS: Record<1 | 2 | 3, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
};

export function ChoiceCardGroup<T extends string>({
  value,
  onValueChange,
  options,
  idPrefix,
  ariaLabel,
  columns = 1,
  disabled = false,
  className,
}: ChoiceCardGroupProps<T>): JSX.Element {
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn("grid gap-3 md:items-stretch", COLUMN_CLASS[columns], className)}
    >
      {options.map((option) => {
        const id = `${idPrefix}-${option.value}`;
        const selected = value === option.value;
        return (
          <div key={option.value} className="flex min-h-0">
            <RadioGroupItem value={option.value} id={id} className="peer sr-only" />
            <Label
              htmlFor={id}
              className={cn(
                // Border stays a utility here on purpose: moving it into the
                // .wizard-modality-option @apply lets it sort after
                // `border-primary` and silently hide the selected state.
                "wizard-modality-option border-2",
                "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
                disabled && "cursor-not-allowed opacity-60",
                selected
                  ? "border-primary bg-primary/[0.06] dark:border-primary dark:bg-primary/10"
                  : "border-border hover:border-primary dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20 dark:hover:border-primary",
              )}
            >
              <span className="text-sm font-semibold leading-snug text-foreground">
                {option.label}
              </span>
              {option.hint ? (
                <span className="text-xs leading-snug text-muted-foreground sm:text-sm">
                  {option.hint}
                </span>
              ) : null}
              {option.footer ? (
                <span
                  className={cn(
                    "mt-auto flex items-center gap-1.5 pt-2 text-[11px] font-medium uppercase tracking-wide transition-colors duration-200",
                    selected ? "text-primary" : "text-muted-foreground/70",
                  )}
                >
                  {option.footer}
                </span>
              ) : null}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
}
